import React, { useState } from "react";
import { useAppContext } from "../../contexts/AppContext";
import Dialog from "carbon-react/lib/components/dialog";
import Detail from "carbon-react/lib/components/detail";
import Heading from "carbon-react/lib/components/heading";
import Content from "carbon-react/lib/components/content";
import TextArea from "carbon-react/lib/__experimental__/components/textarea";
import ErrorMessage from "../messages/ErrorMessage";
import LanguageSelector from "../common/languageSelector/LanguageSelector";
import DialogConfirmButtons from "../common/dialogConfirmButtons/DialogConfirmButtons";

import "./CommentsDialog.css";

const CommentsDialog = (props) => {
  const [errorMsg, setErrorMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [languageCode, setLanguageCode] = useState(null);
  const [validLanguageCode, setValidLanguageCode] = useState(true);
  const [agentComments, setAgentComments] = useState("");
  const [validAgentComments, setValidAgentComments] = useState(true);
  const [performingAction, setPerformingAction] = useState(false);
  const { toastSuccess } = useAppContext();

  const captureLanguageCode =
    props?.dialogState?.captureLanguage &&
    props.dialogState.captureLanguage === true;

  const autoFocusLanguageSelection = captureLanguageCode === true;
  const autoFocusAgentComments = captureLanguageCode !== true;

  const onClose = () => {
    if (performingAction) return;
    setLanguageCode(null);
    setValidLanguageCode(true);
    setAgentComments("");
    setValidAgentComments(true);
    setShowError(false);
    props.onClose();
  };

  const validateAgentComments = () => {
    let valid = true;

    if (agentComments.length === 0) {
      valid = false;
    }

    setValidAgentComments(valid);
    return valid;
  };

  const validateLanguageCode = () => {
    if (!captureLanguageCode) return true;

    let valid = languageCode && languageCode.length > 0;
    setValidLanguageCode(valid);
    return valid;
  };

  const validateInputs = () => {
    let valid = true;
    if (!validateLanguageCode()) valid = false;
    if (!validateAgentComments()) valid = false;
    return valid;
  };

  const onContinue = async () => {
    if (performingAction) return;
    if (!validateInputs()) return;

    try {
      setErrorMsg("");
      setPerformingAction(true);

      const params = {
        agentComments,
      };
      if (captureLanguageCode) params.languageCode = languageCode;

      await props.dialogState.performAction(params);

      raiseSuccessToast();
      onClose();
    } catch (error) {
      if (error) setErrorMsg(error.toString());
      setShowError(true);
    }
    setPerformingAction(false);
  };

  const raiseSuccessToast = () => {
    if (
      props.dialogState &&
      props.dialogState.successToast &&
      props.dialogState.successToast.length > 0
    )
      toastSuccess(props.dialogState.successToast);
  };

  const autoFocusAttribute = { autoFocus: true };

  if (!props.dialogState) return null;

  const validationWarningAttributes = {
    hasWarning: true,
    inputIcon: "warning",
    tooltipMessage: "Please indicate why this action is being performed.",
  };

  const languageSelection = (
    <LanguageSelector
      required={false}
      appearBusy={performingAction}
      value={languageCode}
      onValueChanged={(newLanguage) => {
        setLanguageCode(newLanguage);
      }}
      onBlur={validateLanguageCode}
      appearInvalid={!validLanguageCode}
      validationMsg="Language must be provided"
      {...(autoFocusLanguageSelection ? autoFocusAttribute : {})}
    />
  );

  return (
    <Dialog open={props.open} showCloseIcon={true} onCancel={onClose}>
      <Heading title={props.dialogState.title}></Heading>
      <Content title={props.dialogState.userHeading}>
        {props.dialogState.subTitle}
      </Content>
      <Content title={props.dialogState.promptCaption}>
        {props.dialogState.prompts.map((prompt, idx) => {
          return <Detail key={`prompt-${idx}`}>{prompt}</Detail>;
        })}
      </Content>
      {captureLanguageCode ? languageSelection : null}
      <Content title="Comments">
        <TextArea
          name="agent-comments"
          {...(autoFocusAgentComments ? autoFocusAttribute : {})}
          disabled={performingAction}
          value={agentComments}
          characterLimit={"250"}
          enforceCharacterLimit={true}
          placeholder={"Please indicate why this action is being performed."}
          onChange={(e) => {
            setValidAgentComments(true);
            setAgentComments(e.target.value);
          }}
          onBlur={validateAgentComments}
          {...(!validAgentComments ? validationWarningAttributes : {})}
        />
      </Content>
      <DialogConfirmButtons
        appearBusy={performingAction}
        confirmBtnLabel={props.dialogState.buttonCaption}
        onConfirm={onContinue}
        onCancel={onClose}
      />
      {showError !== true ? null : (
        <ErrorMessage
          message={
            errorMsg && errorMsg.length > 0 ? errorMsg : "The operation failed!"
          }
        />
      )}
    </Dialog>
  );
};

export default CommentsDialog;
